import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MASCOT_URL } from '../assets/mascot';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Activity as ActivityIcon, LogIn, LogOut, Clock, User,
  Circle, TrendingUp, RefreshCw, MousePointerClick, Navigation, FileDown, Send, Pencil, CheckCircle, Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABEL = { admin: 'Administrateur', secretary: 'Secrétaire', technician: 'Technicien' };

const isOnline = (u) => {
  if (!u.last_login) return false;
  if (!u.last_logout) return true;
  return new Date(u.last_login) > new Date(u.last_logout);
};

const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const fmtDuration = (secs) => {
  if (!secs || secs < 0) return '—';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h > 0) return `${h}h ${m}min`;
  if (m > 0) return `${m} min`;
  return `${Math.floor(secs)} s`;
};
const fmtRelative = (iso) => {
  if (!iso) return 'Jamais';
  const diff = (Date.now() - new Date(iso).getTime()) / 60000;
  if (diff < 1) return "À l'instant";
  if (diff < 60) return `Il y a ${Math.floor(diff)} min`;
  if (diff < 1440) return `Il y a ${Math.floor(diff / 60)} h`;
  return `Il y a ${Math.floor(diff / 1440)} j`;
};

const hourHistogram = (history) => {
  const buckets = Array(24).fill(0);
  (history || []).filter((h) => h.type === 'login').forEach((h) => {
    const d = new Date(h.timestamp);
    if (!isNaN(d)) buckets[d.getHours()]++;
  });
  return buckets;
};

const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';

const describeEvent = (ev) => {
  const label = ev.label || '';
  switch (ev.action) {
    case 'navigate': return { icon: Navigation, color: 'text-blue-600', text: `A ouvert : ${label}` };
    case 'click': return { icon: MousePointerClick, color: 'text-[#8B7355]', text: `Clic : ${label}` };
    case 'login': return { icon: LogIn, color: 'text-green-600', text: 'Connexion' };
    case 'logout': return { icon: LogOut, color: 'text-slate-500', text: `Déconnexion${ev.meta?.reason === 'inactivity' ? ' (inactivité)' : ev.meta?.reason === 'manual' ? ' (manuelle)' : ''}` };
    case 'telecharge': return { icon: FileDown, color: 'text-indigo-600', text: `Téléchargement rapport${ev.meta?.format ? ' (' + ev.meta.format + ')' : ''}` };
    case 'envoie': return { icon: Send, color: 'text-cyan-600', text: `Envoi rapport${ev.meta?.to ? ' à ' + ev.meta.to : ''}` };
    case 'corrige': return { icon: Pencil, color: 'text-amber-600', text: 'Correction / modification dossier' };
    case 'valide': return { icon: CheckCircle, color: 'text-green-700', text: 'Validation dossier' };
    case 'delete': return { icon: Trash2, color: 'text-red-600', text: 'Suppression intervention' };
    case 'delete_user': return { icon: Trash2, color: 'text-red-600', text: `Suppression compte${ev.meta?.deleted_email ? ' (' + ev.meta.deleted_email + ')' : ''}` };
    default: return { icon: Circle, color: 'text-slate-400', text: `${ev.action} ${label}` };
  }
};

const Activity = () => {
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [logEvents, setLogEvents] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/users/all`),
        axios.get(`${API}/admin/activity-summary`).catch(() => ({ data: [] })),
      ]);
      const sorted = [...r.data].sort((a, b) => (b.last_login || '').localeCompare(a.last_login || ''));
      setUsers(sorted);
      const map = {};
      (s.data || []).forEach((row) => { map[row.user_id] = row; });
      setSummary(map);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selected) { setLogEvents([]); return; }
    let active = true;
    setLogLoading(true);
    axios.get(`${API}/admin/activity-log?user_id=${selected}&limit=300`)
      .then((res) => { if (active) setLogEvents(res.data || []); })
      .catch(() => { if (active) setLogEvents([]); })
      .finally(() => { if (active) setLogLoading(false); });
    return () => { active = false; };
  }, [selected]);

  // regroupe les événements de traçabilité par jour
  const groupedLog = logEvents.reduce((acc, ev) => {
    const day = (ev.timestamp || '').slice(0, 10);
    (acc[day] = acc[day] || []).push(ev);
    return acc;
  }, {});

  const onlineCount = users.filter(isOnline).length;
  const sel = selected ? users.find((u) => u.id === selected) : null;
  const histo = sel ? hourHistogram(sel.login_history) : [];
  const maxHour = Math.max(1, ...histo);
  const peakHour = histo.indexOf(Math.max(...histo));
  const totalLogins = sel ? (sel.login_history || []).filter((h) => h.type === 'login').length : 0;

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="activity-page">
      <header className="bg-gradient-to-r from-[#1A1A1A] via-[#2D2D2D] to-[#1A1A1A] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" className="text-white hover:bg-[#8B7355]/20" onClick={() => nav('/admin')} data-testid="activity-back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" />Retour
          </Button>
          <h1 className="text-white font-bold text-lg flex items-center gap-2"><ActivityIcon className="h-5 w-5 text-[#8B7355]" />Activité des comptes</h1>
          <Button variant="outline" onClick={load} className="border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white" data-testid="activity-refresh-btn">
            <RefreshCw className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Actualiser</span>
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          <Card className="p-4 border-[#8B7355]/20">
            <p className="text-sm text-[#6B7280]">Comptes</p>
            <p className="text-2xl font-bold text-[#1A1A1A]" data-testid="stat-users">{users.length}</p>
          </Card>
          <Card className="p-4 border-green-200 bg-green-50/40">
            <p className="text-sm text-[#6B7280]">En ligne</p>
            <p className="text-2xl font-bold text-green-700 flex items-center gap-2" data-testid="stat-online"><Circle className="h-3 w-3 fill-green-500 text-green-500" />{onlineCount}</p>
          </Card>
          <Card className="p-4 border-[#8B7355]/20 col-span-2 sm:col-span-1">
            <p className="text-sm text-[#6B7280]">Connexions totales</p>
            <p className="text-2xl font-bold text-[#8B7355]">{users.reduce((s, u) => s + (u.login_history || []).filter((h) => h.type === 'login').length, 0)}</p>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Users list */}
          <div className="lg:col-span-2 space-y-2">
            <h2 className="text-sm font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Utilisateurs</h2>
            {loading ? <p className="text-[#6B7280]">Chargement...</p> : users.map((u) => {
              const online = isOnline(u);
              return (
                <Card key={u.id} onClick={() => setSelected(u.id)}
                  className={`p-3 cursor-pointer transition-all hover:shadow-md ${sel?.id === u.id ? 'border-[#8B7355] ring-1 ring-[#8B7355]' : 'border-[#8B7355]/10'}`}
                  data-testid={`activity-user-${u.email}`}>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-[#8B7355]/15 flex items-center justify-center">
                        <User className="h-5 w-5 text-[#8B7355]" />
                      </div>
                      <Circle className={`absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 ${online ? 'fill-green-500 text-green-500' : 'fill-slate-300 text-slate-300'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#1A1A1A] truncate">{u.name}</p>
                      <p className="text-xs text-[#6B7280]">{ROLE_LABEL[u.role] || u.role}</p>
                    </div>
                    <div className="text-right">
                      <Badge className={online ? 'bg-green-100 text-green-700 border-green-300 border' : 'bg-slate-100 text-slate-500 border-slate-200 border'}>
                        {online ? 'En ligne' : 'Hors ligne'}
                      </Badge>
                      <p className="text-[11px] text-[#6B7280] mt-1">{fmtRelative(u.last_login)}</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Detail */}
          <div className="lg:col-span-3">
            {!sel ? (
              <Card className="p-10 text-center border-dashed border-2 border-[#8B7355]/20 h-full flex flex-col items-center justify-center">
                <img src={MASCOT_URL} alt="Mascotte KARDIF" className="h-32 w-auto mb-3 drop-shadow" />
                <p className="text-[#6B7280]">Sélectionnez un utilisateur pour voir son activité</p>
              </Card>
            ) : (
              <div className="space-y-4" data-testid="activity-detail">
                <Card className="p-5 border-[#8B7355]/20">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <h3 className="text-xl font-bold text-[#1A1A1A]">{sel.name}</h3>
                      <p className="text-sm text-[#6B7280]">{sel.email} · {ROLE_LABEL[sel.role] || sel.role}</p>
                    </div>
                    <Badge className={isOnline(sel) ? 'bg-green-100 text-green-700 border-green-300 border' : 'bg-slate-100 text-slate-500 border-slate-200 border'}>
                      {isOnline(sel) ? 'En ligne maintenant' : 'Hors ligne'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-4">
                    <div className="bg-[#F5F0EB] rounded-lg p-3">
                      <p className="text-xs text-[#6B7280] flex items-center gap-1"><LogIn className="h-3 w-3" />Dernière connexion</p>
                      <p className="text-sm font-semibold text-[#1A1A1A] mt-1">{fmtDateTime(sel.last_login)}</p>
                    </div>
                    <div className="bg-[#F5F0EB] rounded-lg p-3">
                      <p className="text-xs text-[#6B7280] flex items-center gap-1"><LogOut className="h-3 w-3" />Dernière déconnexion</p>
                      <p className="text-sm font-semibold text-[#1A1A1A] mt-1">{fmtDateTime(sel.last_logout)}</p>
                    </div>
                    <div className="bg-[#F5F0EB] rounded-lg p-3">
                      <p className="text-xs text-[#6B7280] flex items-center gap-1"><TrendingUp className="h-3 w-3" />Total connexions</p>
                      <p className="text-sm font-semibold text-[#1A1A1A] mt-1">{totalLogins}</p>
                    </div>
                  </div>
                </Card>

                {/* Statistiques métier détaillées (c1 + c2) */}
                {summary[sel.id] && (
                  <Card className="p-5 border-[#8B7355]/20" data-testid="activity-business-stats">
                    <h4 className="font-semibold text-[#1A1A1A] flex items-center gap-2 mb-3"><TrendingUp className="h-4 w-4 text-[#8B7355]" />Activité détaillée</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div className="bg-[#F5F0EB] rounded-lg p-3">
                        <p className="text-xs text-[#6B7280]">Temps connecté (total)</p>
                        <p className="text-lg font-bold text-[#8B7355] mt-1">{fmtDuration(summary[sel.id].total_connected_seconds)}</p>
                      </div>
                      <div className="bg-[#F5F0EB] rounded-lg p-3">
                        <p className="text-xs text-[#6B7280]">Inactivité</p>
                        <p className="text-lg font-bold text-[#1A1A1A] mt-1">{summary[sel.id].online ? 'En ligne' : fmtDuration(summary[sel.id].inactivity_seconds)}</p>
                      </div>
                      <div className="bg-[#F5F0EB] rounded-lg p-3">
                        <p className="text-xs text-[#6B7280]">Dossiers traités</p>
                        <p className="text-lg font-bold text-[#1A1A1A] mt-1">{summary[sel.id].dossiers_traites}</p>
                      </div>
                      <div className="bg-[#F5F0EB] rounded-lg p-3">
                        <p className="text-xs text-[#6B7280]">Dossiers corrigés</p>
                        <p className="text-lg font-bold text-[#1A1A1A] mt-1">{summary[sel.id].dossiers_corriges}</p>
                      </div>
                      <div className="bg-[#F5F0EB] rounded-lg p-3">
                        <p className="text-xs text-[#6B7280]">Rapports téléchargés</p>
                        <p className="text-lg font-bold text-[#1A1A1A] mt-1">{summary[sel.id].telechargements}</p>
                      </div>
                      <div className="bg-[#F5F0EB] rounded-lg p-3">
                        <p className="text-xs text-[#6B7280]">Rapports envoyés</p>
                        <p className="text-lg font-bold text-[#1A1A1A] mt-1">{summary[sel.id].envois}</p>
                      </div>
                    </div>
                    {sel.role !== 'technician' && (
                      <p className="text-xs text-[#6B7280] mt-2">Dossiers validés : <span className="font-semibold">{summary[sel.id].dossiers_valides}</span> · Suppressions : <span className="font-semibold">{summary[sel.id].suppressions}</span></p>
                    )}
                  </Card>
                )}

                {/* Peak hours histogram */}
                <Card className="p-5 border-[#8B7355]/20">
                  <h4 className="font-semibold text-[#1A1A1A] flex items-center gap-2 mb-1"><Clock className="h-4 w-4 text-[#8B7355]" />Heures de plus forte activité</h4>
                  {totalLogins > 0 ? (
                    <>
                      <p className="text-sm text-[#8B7355] font-medium mb-3">Pic d'activité vers {String(peakHour).padStart(2, '0')}h00</p>
                      <div className="flex items-end gap-[3px] h-32" data-testid="activity-histogram">
                        {histo.map((v, h) => (
                          <div key={h} className="flex-1 flex flex-col items-center justify-end group" title={`${String(h).padStart(2, '0')}h : ${v} connexion(s)`}>
                            <div className={`w-full rounded-t transition-all ${v === maxHour && v > 0 ? 'bg-[#8B7355]' : 'bg-[#8B7355]/30'} group-hover:bg-[#8B7355]`}
                              style={{ height: `${(v / maxHour) * 100}%`, minHeight: v > 0 ? '4px' : '0' }} />
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between text-[10px] text-[#6B7280] mt-1">
                        <span>00h</span><span>06h</span><span>12h</span><span>18h</span><span>23h</span>
                      </div>
                    </>
                  ) : <p className="text-sm text-[#6B7280]">Aucune connexion enregistrée pour le moment.</p>}
                </Card>

                {/* Connection log */}
                <Card className="p-5 border-[#8B7355]/20">
                  <h4 className="font-semibold text-[#1A1A1A] mb-3">Journal des connexions (50 dernières)</h4>
                  <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="activity-log">
                    {(sel.login_history || []).length === 0 ? (
                      <p className="text-sm text-[#6B7280]">Aucun évènement.</p>
                    ) : (
                      [...(sel.login_history || [])].reverse().slice(0, 50).map((h, i) => (
                        <div key={i} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0 text-sm">
                          {h.type === 'login'
                            ? <LogIn className="h-4 w-4 text-green-600 shrink-0" />
                            : <LogOut className="h-4 w-4 text-slate-400 shrink-0" />}
                          <span className={`font-medium ${h.type === 'login' ? 'text-green-700' : 'text-slate-500'}`}>
                            {h.type === 'login' ? 'Connexion' : `Déconnexion${h.reason === 'inactivity' ? ' (inactivité)' : h.reason === 'manual' ? ' (manuelle)' : ''}`}
                          </span>
                          <span className="text-[#6B7280] ml-auto">{fmtDateTime(h.timestamp)}</span>
                        </div>
                      ))
                    )}
                  </div>
                </Card>

                {/* Journal de traçabilité détaillé (navigation, clics, actions) */}
                <Card className="p-5 border-[#8B7355]/20" data-testid="activity-trace-log">
                  <h4 className="font-semibold text-[#1A1A1A] mb-1 flex items-center gap-2"><MousePointerClick className="h-4 w-4 text-[#8B7355]" />Traçabilité détaillée</h4>
                  <p className="text-xs text-[#6B7280] mb-3">Chaque page ouverte, clic important et action, avec l'heure exacte (300 derniers événements).</p>
                  {logLoading ? (
                    <p className="text-sm text-[#6B7280]">Chargement...</p>
                  ) : logEvents.length === 0 ? (
                    <p className="text-sm text-[#6B7280]">Aucun événement enregistré pour le moment. La traçabilité se remplit dès que l'utilisateur navigue dans l'application.</p>
                  ) : (
                    <div className="max-h-96 overflow-y-auto pr-1">
                      {Object.keys(groupedLog).sort((a, b) => b.localeCompare(a)).map((day) => (
                        <div key={day} className="mb-3">
                          <p className="text-[11px] font-semibold text-[#8B7355] uppercase tracking-wide sticky top-0 bg-white py-1">
                            {day ? new Date(day).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) : 'Date inconnue'}
                          </p>
                          <div className="relative pl-4 border-l-2 border-[#8B7355]/15">
                            {groupedLog[day].map((ev, i) => {
                              const d = describeEvent(ev);
                              const Icon = d.icon;
                              return (
                                <div key={ev.id || i} className="flex items-start gap-2 py-1.5 text-sm" data-testid="trace-event">
                                  <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${d.color}`} />
                                  <span className="text-[#1A1A1A] flex-1 leading-tight">{d.text}</span>
                                  <span className="text-[11px] text-[#6B7280] font-mono shrink-0">{fmtTime(ev.timestamp)}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Activity;
